import { useTranslation } from "react-i18next";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import {
  Layout,
  LayoutContent,
  LayoutHeader,
  LayoutHeaderActions,
  LayoutHeaderTitle,
} from "../../../../general/modules/commons/layout/layout";
import { Content } from "../../../../general/modules/commons/content/content";
import { ContentHeader } from "../../../../general/modules/commons/content/contentHeader";
import { packingSlipSchema } from "../../domain/schema/packingSlipSchema";
import { logout } from "../../../../auth/infrastructure/slices/authSlice/authSlice";
import {
  Breadcrumb,
  BreadCrumbItem,
} from "../../../../general/modules/commons/breadcrumb/breadcrumb";
import { Link, useParams } from "react-router-dom";
import { PackingSlipModel } from "../../../dailyPlanning/domain/models/packingSlipModel";
import useCrudForm from "../../../../../hooks/useCrudForm";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { CrudTable } from "../../../../general/modules/commons/crud/crudTable";
import {
  SlipRequestTableColumns,
  SlipPickupRequestTableColumns,
} from "../../domain/columns/SlipRequestTableColumns";
import { PackingSlipTableCommentRow } from "../features/PackingSlipTableCommentRow";
import { Button, Col } from "react-bootstrap";
import { Row } from "react-bootstrap";
import { CrudPlaceholderInput } from "../../../../general/modules/commons/crud/inputs/crudPlaceholderInput";
import React, { useEffect, useMemo, useState } from "react";
import { PackingSlipTableStatusRow } from "../features/PackingSlipTableStatusRow";
import { shallowEqual } from "react-redux";
import { convertDateTimeStringToLocate } from "../../../../general/date/infrastructure/helpers/DateConverter";
import { PackingSlipExportButton } from "../features/PackingSlipExportButton";
import moment from "moment/moment";
import LicensePlate from "license-plate";
import { BulkActions } from "../../../../general/modules/commons/bulk/BulkActions";
import { AllRequestQueryResponseModel } from "../../../../request/domain/models/requestModel";
import { getAllRequestStates } from "../../../../request/infrastructure/slices/requestMutateSlice";
import type { Table } from "@tanstack/react-table";
import { useAuthorization } from "../../../../../hooks/useAuthorization";

const CrudForm = () => {
  return (
    <Row>
      <Col xs={12}>
        <CrudPlaceholderInput name="comment" />
      </Col>
    </Row>
  );
};

export const PackingSlipDetailPage = () => {
  const { t } = useTranslation("common");
  const { hasRole } = useAuthorization();
  const title = t("Packing slip");
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const { id } = useParams();
  const model = useAppSelector(
    (state) => state.crudMutate.model as PackingSlipModel,
    shallowEqual,
  );

  useCrudForm({
    schema: packingSlipSchema,
    prefix: "packingSlips",
    createMutation: "upsertPackingSlip",
    updateMutation: "upsertPackingSlip",
    id: id ? parseInt(id) : undefined,
    formTemplate: CrudForm,
  });

  const memoizedRequests = useMemo(
    () => model.requests ?? [],
    [model.requests],
  );

  const deliverRequests = useMemo(() => {
    return memoizedRequests.filter((r) => r.type === "deliver");
  }, [memoizedRequests]);

  const pickupRequests = useMemo(() => {
    return memoizedRequests.filter((r) => r.type === "pickup") ?? [];
  }, [memoizedRequests]);

  const [selectedDeliverIds, setSelectedDeliverIds] = useState<string[]>([]);
  const [selectedPickupIds, setSelectedPickupIds] = useState<string[]>([]);
  const [showDeliverBulkPopup, setShowDeliverBulkPopup] = useState(false);
  const [showPickupBulkPopup, setShowPickupBulkPopup] = useState(false);
  const pageName = "packingSlipDetail";

  const [allRequestStates, setAllRequestStates] = useState<
    AllRequestQueryResponseModel | undefined
  >();
  useEffect(() => {
    if (!allRequestStates) {
      void dispatch(getAllRequestStates())
        .unwrap()
        .then((response) => {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setAllRequestStates(response);
        });
    }
  }, []);
  const buildDeliverStateOptions = () => {
    if (allRequestStates) {
      return allRequestStates.deliverStates.map((state) => {
        return {
          value: state,
          label: state,
        };
      });
    }
  };
  const buildPickupStateOptions = () => {
    if (allRequestStates) {
      return allRequestStates.pickupStates.map((state) => {
        return {
          value: state,
          label: state,
        };
      });
    }
  };

  const toggleDeliverBulkPopup = () => setShowDeliverBulkPopup((v) => !v);
  const togglePickupBulkPopup = () => setShowPickupBulkPopup((v) => !v);

  const clearSelection = (type: "deliver" | "pickup") => {
    if (type === "deliver") setSelectedDeliverIds([]);
    else setSelectedPickupIds([]);
  };

  const renderBulkActions = (type: "deliver" | "pickup") => {
    const isDeliver = type === "deliver";
    const selectedIds = isDeliver ? selectedDeliverIds : selectedPickupIds;
    const otherSelected = isDeliver ? selectedPickupIds : selectedDeliverIds;
    if (selectedIds.length === 0 || otherSelected.length > 0) return null;
    return (
      <div className="mb-2 d-flex gap-2">
        <Button size="sm" variant="danger" onClick={() => clearSelection(type)}>
          {t("Clear selection") + ` (${selectedIds.length})`}
        </Button>
        <Button
          size="sm"
          variant="danger"
          onClick={isDeliver ? toggleDeliverBulkPopup : togglePickupBulkPopup}
        >
          {t("Bulk form")}
        </Button>
      </div>
    );
  };

  // Custom select handlers zodat altijd maar één selectie actief is
  const handleDeliverSelect = (ids: string[]) => {
    setSelectedDeliverIds(ids);
    setSelectedPickupIds([]);
  };
  const handlePickupSelect = (ids: string[]) => {
    setSelectedPickupIds(ids);
    setSelectedDeliverIds([]);
  };

  const tableDeliver = useReactTable({
    columns: SlipRequestTableColumns,
    data: deliverRequests,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      selectedIds: selectedDeliverIds,
      setSelectedIds: handleDeliverSelect,
    },
  });

  const tablePickup = useReactTable({
    columns: hasRole("dealer-employee")
      ? SlipRequestTableColumns
      : SlipPickupRequestTableColumns,
    data: pickupRequests,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      selectedIds: selectedPickupIds,
      setSelectedIds: handlePickupSelect,
    },
  });

  if (!currentUser) {
    return (
      <Layout>
        <LayoutContent>
          <Content>
            <ContentHeader className={"mt-3"} title={t("Access denied")} />
            <p className={"mt-3"}>{t("You don't have access to this page")}</p>
          </Content>
        </LayoutContent>
      </Layout>
    );
  }

  const isDealerEmployee = useMemo(
    () => currentUser?.role === "dealer-employee",
    [currentUser?.role],
  );

  return (
    <Layout
      header={
        <LayoutHeader logout={() => void dispatch(logout())}>
          <LayoutHeaderTitle title={title + " #" + id} />
          <Breadcrumb>
            <BreadCrumbItem>{title}</BreadCrumbItem>
          </Breadcrumb>

          <LayoutHeaderActions>
            <div className="d-flex gap-2">
              <PackingSlipExportButton packingSlip={model} />
            </div>
          </LayoutHeaderActions>
        </LayoutHeader>
      }
    >
      <LayoutContent>
        <Content md={6}>
          <ContentHeader title={t("Location")} className="mb-3" />
          <table>
            <tbody>
              <tr>
                <td>{t("Dealer")}:</td>
                <td className={"fw-bold"}>{model.relation?.name}</td>
              </tr>

              <tr>
                <td className={"align-top"}>{t("Address")}:</td>
                <td>
                  <div className={"d-flex flex-column"}>
                    <span>
                      {model.relation?.street} {model.relation?.house_number}{" "}
                      {model.relation?.house_number_addition}
                    </span>
                    <span>{model.relation?.postal_code}</span>
                    <span>{model.relation?.city}</span>
                  </div>
                </td>
              </tr>

              <tr>
                <td>{t("E-mail")}:</td>
                <td> -</td>
              </tr>

              <tr>
                <td>{t("Phone number")}:</td>
                <td> {model.relation?.phone} </td>
              </tr>
            </tbody>
          </table>
        </Content>
        <Content md={6}>
          <ContentHeader title={t("Details")} className="mb-3" />
          <table>
            <tbody>
              <tr>
                <td>{t("Packing slip")}:</td>
                <td>{model.id}</td>
              </tr>

              <tr>
                <td>{t("Date")}:</td>
                <td>{moment(model.date).format("DD-MM-YYYY")}</td>
              </tr>

              <tr>
                <td>{t("Truck")}:</td>
                <td>
                  {" "}
                  {new LicensePlate(
                    model.route?.truck?.licence ?? "",
                  ).pretty() ?? model.route?.truck?.title}
                </td>
              </tr>

              <tr>
                <td>{t("Route number")}:</td>
                <td> {(model.route?.delta ?? 0) + 1} </td>
              </tr>

              {!isDealerEmployee ? (
                <PackingSlipTableCommentRow />
              ) : (
                <tr>
                  <td>{t("Comment")}:</td>
                  <td>{model.comment}</td>
                </tr>
              )}
              {!isDealerEmployee ? (
                <PackingSlipTableStatusRow />
              ) : (
                <tr>
                  <td>{t("Status")}:</td>
                  <td>{t(model.status ?? "")}</td>
                </tr>
              )}
            </tbody>
          </table>
        </Content>
        <Content md={6}>
          <div
            className={"d-flex align-items-center justify-content-between mb-3"}
          >
            <ContentHeader title={t("Deliver requests")} />
            <div>
              {model.deliverSticker && (
                <Link to={model.deliverSticker?.pdfUrl} target={"_blank"}>
                  <Button size="sm">{t("sticker.open")}</Button>
                </Link>
              )}
            </div>
          </div>
          {renderBulkActions("deliver")}
          <CrudTable
            table={tableDeliver as unknown as Table<Record<string, unknown>>}
            prefix={"requests"}
            disablePagination={true}
            disableSelectAll={true}
          />
          <BulkActions
            entity="Request"
            bulkFields={[
              { field: "in_out_storage", options: [], date: true },
              {
                field: "state_deliver",
                options: buildDeliverStateOptions() ?? [],
                date: false,
              },
              {
                field: "invoice_status",
                options: [
                  { value: "invoiced", label: "invoiced" },
                  { value: "to_invoice", label: "to_invoice" },
                ],
                date: false,
              },
            ]}
            selectedIds={selectedDeliverIds}
            pageName={pageName}
            showBulkPopup={showDeliverBulkPopup}
            toggleBulkPopup={toggleDeliverBulkPopup}
          />
        </Content>
        <Content md={6}>
          <div
            className={"d-flex align-items-center justify-content-between mb-3"}
          >
            <ContentHeader title={t("Pickup requests")} />
            <div>
              {model.pickupSticker && (
                <Link to={model.pickupSticker?.pdfUrl} target={"_blank"}>
                  <Button size="sm">{t("sticker.open")}</Button>
                </Link>
              )}
            </div>
          </div>
          <div className="mb-3">
            <span className={"me-3"}>{t("Pickup amount")}</span>
            <span className={"fw-bold"}>{model.pickup_amount ?? 0}</span>
          </div>
          {renderBulkActions("pickup")}
          <CrudTable
            table={tablePickup as unknown as Table<Record<string, unknown>>}
            prefix={"requests"}
            disablePagination={true}
            disableSelectAll={true}
          />
          <BulkActions
            entity="Request"
            bulkFields={[
              { field: "in_out_storage", options: [], date: true },
              {
                field: "state_pickup",
                options: buildPickupStateOptions() ?? [],
                date: false,
              },
              {
                field: "invoice_status",
                options: [
                  { value: "invoiced", label: "invoiced" },
                  { value: "to_invoice", label: "to_invoice" },
                ],
                date: false,
              },
            ]}
            selectedIds={selectedPickupIds}
            pageName={pageName}
            showBulkPopup={showPickupBulkPopup}
            toggleBulkPopup={togglePickupBulkPopup}
          />
        </Content>
        {model.signature_time && (
          <Content md={12}>
            <div className="d-flex align-items-center mb-3">
              <ContentHeader title={t("Signature")} />

              <div className="ms-4">
                <span style={{ fontSize: "12px" }}>
                  {convertDateTimeStringToLocate(model.signature_time)}
                </span>
              </div>
            </div>

            <div>
              {model.signature && (
                <img
                  src={model.signature}
                  alt="Signature"
                  className="img-fluid"
                  style={{ width: "400px" }}
                />
              )}
            </div>
          </Content>
        )}
      </LayoutContent>
    </Layout>
  );
};
